import type { FC } from 'react';

import { Box, Fade, type SxProps, Typography, useMediaQuery } from '@mui/material';
import { colors } from '@truvhq/ui';
import { clsx } from 'clsx';

import { useSandboxContext } from 'shared/lib/sandbox/useSandboxContext';

type SandboxHeaderProps = {
  absolute?: boolean;
  isEmulator?: boolean;
};

export const SandboxHeader: FC<SandboxHeaderProps> = ({ absolute, isEmulator = false }) => {
  const { isSandboxOn } = useSandboxContext();
  const isCompact = useMediaQuery('(max-width:1280px)');

  if (!isSandboxOn) {
    return null;
  }

  const bannerColor = 'black';
  const capBase: SxProps = {
    content: '""',
    position: 'absolute',
    top: '1px',
    height: '32px',
    width: '32px',
    backgroundColor: 'transparent',
  } as const;

  return (
    <Fade in={isSandboxOn} timeout={600}>
      <div
        className={clsx('pointer-events-none flex w-full flex-col items-center', {
          'absolute z-20': absolute,
        })}
        data-testid="sandbox-header"
      >
        <div className="h-0.5 w-full min-w-[197px]" style={{ backgroundColor: bannerColor }} />
        <Box
          className="mt-[-1px] relative flex h-[32px] items-center rounded-2xl justify-center px-4"
          sx={{
            color: bannerColor,
            backgroundColor: 'currentColor',
            width: 'fit-content',
            minWidth: 'max-content',
            '&::before': {
              ...capBase,
              right: '-32px',
              borderTopLeftRadius: '16px',
              boxShadow: '-12px -16px 0 0',
            },
            '&::after': {
              ...capBase,
              left: '-32px',
              borderTopRightRadius: '16px',
              boxShadow: '12px -16px 0 0',
            },
          }}
        >
          <Typography className="whitespace-nowrap font-medium text-white" variant="body2">
            You're using <strong style={{ color: colors.orange.main }}>sandbox mode</strong>
            {isCompact || isEmulator ? '' : '. No real transactions will be processed.'}
          </Typography>
        </Box>
      </div>
    </Fade>
  );
};
